import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  FormHelperText,
  Slider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AuthLayout from '../components/AuthLayout';
import slaService, { SLA } from '../services/sla.service';

const SLAForm: React.FC = () => {
  const { slaId } = useParams<{ slaId: string }>();
  const navigate = useNavigate();
  const isEditMode = slaId !== 'new';
  
  const [formData, setFormData] = useState<Partial<SLA>>({
    name: '',
    description: '',
    responseTime: 24,
    resolutionTime: 48,
    priorityMultipliers: {
      low: 1.5,
      medium: 1,
      high: 0.75,
      critical: 0.5
    },
    isActive: true
  });
  
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  useEffect(() => {
    if (isEditMode && slaId && slaId !== 'new') {
      console.log(`Carregando SLA com ID: ${slaId}`);
      fetchSLA();
    }
  }, [slaId, isEditMode]);
  
  const fetchSLA = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!slaId || slaId === 'new') {
        throw new Error('ID do SLA inválido');
      }
      
      const sla = await slaService.getSLAById(slaId);
      console.log('SLA carregado:', sla);
      
      // Garantir que o _id está presente no formData
      if (sla && !sla._id && slaId) {
        sla._id = slaId;
      }
      
      setFormData(sla);
    } catch (err: any) {
      console.error('Error fetching SLA:', err);
      setError(`Falha ao carregar dados do SLA: ${err.message || 'Por favor, tente novamente.'}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    
    if (!name) return;
    
    if (name.includes('.')) {
      // Handle nested properties (priorityMultipliers)
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof SLA] as any || {}),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSliderChange = (name: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      priorityMultipliers: {
        ...prev.priorityMultipliers!,
        [name]: value
      }
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaveLoading(true);
      setError('');
      setSuccess(false);
      
      // Verificar se estamos no modo de edição e se o ID é válido
      if (isEditMode && slaId && slaId !== 'new') {
        console.log(`Tentando atualizar SLA com ID: ${slaId}`);
        
        // Criar uma cópia dos dados do formulário para atualização
        const dataToUpdate = { ...formData };
        
        // Garantir que o _id esteja presente e seja o mesmo que o slaId
        if (!dataToUpdate._id) {
          dataToUpdate._id = slaId;
          console.log(`Adicionando _id ${slaId} aos dados para atualização`);
        }
        
        // Remover campos que não devem ser enviados na atualização
        delete dataToUpdate.createdAt;
        delete dataToUpdate.updatedAt;
        
        console.log('Dados para atualização:', dataToUpdate);
        
        // Chamar o serviço para atualizar o SLA
        const result = await slaService.updateSLA(slaId, dataToUpdate);
        console.log('Resultado da atualização:', result);
      } else {
        // Estamos no modo de criação
        console.log('Criando novo SLA');
        
        // Remover campos que não devem ser enviados na criação
        const dataToCreate = { ...formData };
        delete dataToCreate._id;
        delete dataToCreate.createdAt;
        delete dataToCreate.updatedAt;
        
        console.log('Dados para criação:', dataToCreate);
        
        // Chamar o serviço para criar o SLA
        const result = await slaService.createSLA(dataToCreate as Omit<SLA, '_id' | 'createdAt' | 'updatedAt'>);
        console.log('Resultado da criação:', result);
      }
      
      setSuccess(true);
      
      // Redirect to SLA list after a short delay
      setTimeout(() => {
        navigate('/slas');
      }, 1500);
    } catch (err: any) {
      console.error('Error saving SLA:', err);
      setError(`Falha ao salvar SLA: ${err.message || 'Por favor, tente novamente.'}`);
    } finally {
      setSaveLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/slas');
  };
  
  if (loading) {
    return (
      <AuthLayout title="Carregando SLA">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} thickness={4} sx={{ color: '#2e7d32' }} />
        </Box>
      </AuthLayout>
    );
  }
  
  return (
    <AuthLayout title={isEditMode ? 'Editar SLA' : 'Novo SLA'}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/slas'); }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography color="text.primary">SLAs</Typography>
          </Link>
          <Typography color="text.primary">
            {isEditMode ? 'Editar SLA' : 'Novo SLA'}
          </Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            {isEditMode ? 'Editar SLA' : 'Novo SLA'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
          >
            Voltar
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            SLA {isEditMode ? 'atualizado' : 'criado'} com sucesso!
          </Alert>
        )}
        
        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Informações Básicas
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Nome do SLA"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  required
                  label="Tempo de Resposta (horas)"
                  name="responseTime"
                  type="number"
                  value={formData.responseTime || 24}
                  onChange={handleInputChange}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  required
                  label="Tempo de Resolução (horas)"
                  name="resolutionTime"
                  type="number"
                  value={formData.resolutionTime || 48}
                  onChange={handleInputChange}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth required>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    name="isActive"
                    value={formData.isActive === undefined ? true : formData.isActive}
                    // @ts-ignore - Incompatibilidade de tipos entre React.ChangeEvent e SelectChangeEvent
                    onChange={handleInputChange}
                    label="Status"
                  >
                    {/* @ts-ignore - Valor booleano não é atribuível ao tipo esperado */}
                    <MenuItem value={"true"}>Ativo</MenuItem>
                    {/* @ts-ignore - Valor booleano não é atribuível ao tipo esperado */}
                    <MenuItem value={"false"}>Inativo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" sx={{ mb: 2 }}>
              Multiplicadores por Prioridade
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Defina os multiplicadores de tempo para cada nível de prioridade. Valores menores que 1 reduzem o tempo, valores maiores que 1 aumentam o tempo.
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>
                  Baixa: {formData.priorityMultipliers?.low || 1.5}x
                </Typography>
                <Slider
                  value={formData.priorityMultipliers?.low || 1.5}
                  onChange={(_, value) => handleSliderChange('low', value as number)}
                  step={0.1}
                  marks
                  min={1}
                  max={2}
                  valueLabelDisplay="auto"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>
                  Média: {formData.priorityMultipliers?.medium || 1}x
                </Typography>
                <Slider
                  value={formData.priorityMultipliers?.medium || 1}
                  onChange={(_, value) => handleSliderChange('medium', value as number)}
                  step={0.1}
                  marks
                  min={0.5}
                  max={1.5}
                  valueLabelDisplay="auto"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>
                  Alta: {formData.priorityMultipliers?.high || 0.75}x
                </Typography>
                <Slider
                  value={formData.priorityMultipliers?.high || 0.75}
                  onChange={(_, value) => handleSliderChange('high', value as number)}
                  step={0.05}
                  marks
                  min={0.5}
                  max={1}
                  valueLabelDisplay="auto"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>
                  Crítica: {formData.priorityMultipliers?.critical || 0.5}x
                </Typography>
                <Slider
                  value={formData.priorityMultipliers?.critical || 0.5}
                  onChange={(_, value) => handleSliderChange('critical', value as number)}
                  step={0.05}
                  marks
                  min={0.1}
                  max={0.75}
                  valueLabelDisplay="auto"
                />
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ mr: 2 }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                disabled={saveLoading}
              >
                {saveLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </AuthLayout>
  );
};

export default SLAForm;
